import React, { forwardRef, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./RichTextEditor.css";
import { cn } from "./cn";

/** Quill presentation only. Document loading, saving, uploads and dictation belong to callers. */
const RichTextEditor = forwardRef(function RichTextEditor(
  { ariaLabel = "Rich text editor", status, overlay, actions, className, ...props }, ref
) {
  const wrapperRef = useRef(null);
  useEffect(() => {
    const root = wrapperRef.current;
    const editor = root?.querySelector(".ql-editor");
    editor?.setAttribute("role", "textbox");
    editor?.setAttribute("aria-multiline", "true");
    editor?.setAttribute("aria-label", ariaLabel);
    root?.querySelectorAll(".ql-toolbar button").forEach((button) => {
      const format = [...button.classList].find((name) => name.startsWith("ql-"))?.slice(3);
      if (!format) return;
      button.setAttribute("type", "button");
      button.setAttribute("aria-label", `${format}${button.value ? ` ${button.value}` : ""}`);
    });
    root?.querySelectorAll(".ql-toolbar .ql-picker-label").forEach((picker) => {
      picker.setAttribute("aria-label", "Heading level");
    });
  }, [ariaLabel]);

  return (
    <div ref={wrapperRef} className={cn("bun-rich-text min-w-0 font-instrument text-bun-text", className)}>
      <div className="relative">
        <ReactQuill ref={ref} {...props} />
        {overlay && <div className="absolute right-3 top-3 z-10 w-72 max-w-[calc(100%-1.5rem)]">{overlay}</div>}
      </div>
      {(status || actions) && (
        <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
          {status && (
            <div role="status" aria-live="polite" className="flex items-center gap-1.5 text-bun-meta text-bun-stone">
              <svg className="h-3 w-3 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
                <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" />
              </svg>
              {status}
            </div>
          )}
          {actions}
        </div>
      )}
    </div>
  );
});

export default RichTextEditor;
